import { isMissing } from "@/profitability/missing";
import { rootFigures } from "@/profitability/sources/figure-roots";
import type { FigureSummary } from "@/profitability/sources/types";
import type {
  ReportColumn,
  ReportDataset,
  ReportRow,
} from "@/profitability/types";
import {
  absentColumns,
  columnWords,
  listWords,
  missingFields,
} from "./missing-fields";

export type GapReason =
  | { kind: "blank" }
  | { kind: "lines"; count: number }
  /** The script's own words (nothing matched, no share). */
  | { kind: "note"; note: string }
  | { kind: "setting"; label: string }
  | { kind: "absent" };

export interface RootGap {
  column: ReportColumn;
  reason: GapReason;
  /** The input (slot key) that fills it; null when no one input does. */
  input: string | null;
}

/** A figure missing only because others are: Burden, worked out from Labor. */
export interface DependentGap {
  column: ReportColumn;
  /** The root gaps it waits on, in the dataset's order. */
  needs: ReportColumn[];
}

export interface FigureGaps {
  roots: RootGap[];
  dependents: DependentGap[];
}

const unset = (figure: FigureSummary | undefined) =>
  figure?.setting_parts.find((part) => isMissing(part.value)) ?? null;

const reasonOf = (figure: FigureSummary | undefined): GapReason => {
  if (!figure) return { kind: "absent" };
  const setting = unset(figure);
  if (setting)
    return { kind: "setting", label: setting.note ?? setting.setting };
  if (figure.how === "cell") return { kind: "blank" };
  if (figure.how === "sum" && figure.count > 0)
    return { kind: "lines", count: figure.count };
  if (figure.note && ["sum", "none", "share"].includes(figure.how))
    return { kind: "note", note: figure.note };
  return { kind: "absent" };
};

/**
 * The input that fills a root gap: the one its blank parts are in (the
 * column's own when they are in several), else, for a figure read straight
 * from a file (a cell, a sum, nothing matched), the column's `source`. A
 * share whose amount is there, or a formula, names none: what is missing
 * there is not a file's cell.
 */
const inputOf = (column: ReportColumn, figure: FigureSummary | undefined) => {
  if (!figure) return column.source ?? null;
  if (unset(figure)) return null;
  const blanks = figure.blank_inputs;
  if (blanks.length === 1) return blanks[0];
  if (blanks.length > 1)
    return column.source && blanks.includes(column.source)
      ? column.source
      : null;
  return ["cell", "sum", "none"].includes(figure.how)
    ? (column.source ?? null)
    : null;
};

/**
 * A record's missing figures, split by how they were made (the record's
 * `figures`, from the run's lineage): a root gap is missing for a reason of
 * its own (a blank cell, every line blank, nothing matched, a setting not
 * set); a dependent is worked out from missing figures, and waits on the
 * roots they trace to. A column the run did not trace falls back to the
 * roles (`missingFields`): a result or rate the role gaps feed waits on them.
 */
export const figureGaps = (
  dataset: ReportDataset,
  row: ReportRow,
  figures: Record<string, FigureSummary>,
): FigureGaps => {
  const absent = absentColumns(dataset, row);
  const missing = new Set(absent.map((column) => column.key));
  const roles = missingFields(dataset, row);
  const blocked = new Set(roles.blocked.map((column) => column.key));
  const missingDeps = (key: string) => {
    const figure = figures[key];
    if (figure) return figure.depends_on.filter((dep) => missing.has(dep));
    return blocked.has(key) ? roles.gaps.map((column) => column.key) : [];
  };
  const roots: RootGap[] = [];
  const dependents: DependentGap[] = [];
  for (const column of absent) {
    const behind = new Set(rootFigures(column.key, missingDeps));
    if (behind.size === 1 && behind.has(column.key)) {
      const figure = figures[column.key];
      roots.push({
        column,
        reason: reasonOf(figure),
        input: inputOf(column, figure),
      });
    } else
      dependents.push({
        column,
        needs: absent.filter((item) => behind.has(item.key)),
      });
  }
  return { roots, dependents };
};

export const gapsTitle = ({ roots }: FigureGaps) =>
  `${listWords(roots.map(({ column }) => columnWords(column)))} ${roots.length === 1 ? "isn't" : "aren't"} in yet`;

/** Lowercase a title-case opening mid-sentence. Leave an acronym. */
const midSentence = (text: string) =>
  /^[A-Z][a-z]/.test(text) ? text[0].toLowerCase() + text.slice(1) : text;

/** `named` puts the figure's name in, except when the note already names it. */
export const rootReason = (
  { column, reason }: RootGap,
  inputTitle: string | null,
  named: boolean,
) => {
  const name = columnWords(column);
  const where = `this period's ${inputTitle || "files"}`;
  switch (reason.kind) {
    case "blank":
      return `${named ? `${name}'s` : "Its"} cell in ${where} is blank`;
    case "lines":
      return reason.count === 1
        ? `${named ? `${name}'s` : "Its"} only line in ${where} is blank`
        : `All ${reason.count} of ${named ? `${name}'s` : "its"} lines in ${where} are blank`;
    case "note": {
      // A note that names the figure ("No share of $217.00 Shop time, by
      // hours") says it mid-sentence, and needs no name before it.
      const note = reason.note
        .trim()
        .replace(/\.$/, "")
        .replace(` ${name},`, ` ${midSentence(name)},`);
      const names = note.toLowerCase().includes(name.toLowerCase());
      return named && !names ? `${name}: ${midSentence(note)}` : note;
    }
    case "setting":
      return `The ${reason.label} setting isn't set`;
    case "absent":
      return named ? `${name} isn't in ${where}` : `It's not in ${where}`;
  }
};

/** Several roots: a sentence each for the first three, then what waits on them. */
export const gapsImpact = (
  { roots, dependents }: FigureGaps,
  titleOf: (input: string | null) => string | null,
) => {
  const waiting = listWords(
    dependents.map(({ column }) => columnWords(column)),
  );
  if (roots.length === 1) {
    const [root] = roots;
    const why = rootReason(root, titleOf(root.input), false);
    return dependents.length
      ? `${why}, so ${waiting} can't be worked out.`
      : `${why}. Totals leave it out until it's added.`;
  }
  const whys = roots
    .slice(0, 3)
    .map((root) => `${rootReason(root, titleOf(root.input), true)}.`);
  return [
    ...whys,
    dependents.length
      ? `${waiting} can't be worked out until they're in.`
      : "Totals leave them out until they're added.",
  ].join(" ");
};

export const needsWords = ({ needs }: DependentGap) =>
  listWords(needs.map(columnWords));

/** Null when the column is not a dependent. */
export const needsOf = (gaps: FigureGaps | null | undefined, key: string) => {
  const dependent = gaps?.dependents.find(({ column }) => column.key === key);
  return dependent ? needsWords(dependent) : null;
};
